import { View, Text, Image, Platform } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import type { DogStats } from "../app/lib/types";

// Resolve a custom ribbon image URL for a given title name. Returns null when
// no custom ribbon exists for that title — caller falls back to the generic
// Ionicons "ribbon" badge.
// PAUSED: design iteration in progress. The full resolver (SVG on web, PNG on
// native) is preserved below for easy re-enable. Toggle CUSTOM_RIBBONS_ENABLED
// to true to bring custom ribbons back.
const CUSTOM_RIBBONS_ENABLED = false;
const RIBBON_BASE = "https://dashhound.app/ribbons";
const RIBBON_EXT = Platform.OS === "web" ? "svg" : "png";
const RIBBON_V = Platform.OS === "web" ? "?v=3" : "?v=1";
function customRibbonFor(name: string): string | null {
  if (!CUSTOM_RIBBONS_ENABLED) return null;
  if (name === "BCAT") return `${RIBBON_BASE}/bcat.${RIBBON_EXT}${RIBBON_V}`;
  if (name === "DCAT") return `${RIBBON_BASE}/dcat.${RIBBON_EXT}${RIBBON_V}`;
  if (name === "FCAT") return `${RIBBON_BASE}/fcat.${RIBBON_EXT}${RIBBON_V}`;
  const m = name.match(/^FCAT(\d+)$/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (n >= 2 && n <= 10) return `${RIBBON_BASE}/fcat-2-10.${RIBBON_EXT}${RIBBON_V}`;
  if (n >= 11 && n <= 25) return `${RIBBON_BASE}/fcat-11-25.${RIBBON_EXT}${RIBBON_V}`;
  if (n >= 26 && n <= 50) return `${RIBBON_BASE}/fcat-26-50.${RIBBON_EXT}${RIBBON_V}`;
  if (n >= 51 && n <= 154) return `${RIBBON_BASE}/fcat-51-plus.${RIBBON_EXT}${RIBBON_V}`;
  if (n >= 155) return `${RIBBON_BASE}/fcat-155-plus.${RIBBON_EXT}${RIBBON_V}`;
  return null;
}

interface TitlesEarnedProps {
  stats: DogStats;
}

interface TitleMilestone {
  name: string;
  points: number;
  color: string;       // Text/label color
  ribbonColor: string; // Ribbon + border color
  tier: "base" | "fcat-low" | "elite" | "champion" | "legend" | "mythic";
  isMilestone?: boolean; // 25, 50, 75, 100, 125, 150, 175, 200 — gets extra treatment
}

// Tier color palettes — each tier gets richer/cooler colors than the last.
// Past FCAT10 we cycle within a tier so consecutive ribbons read as a family.
const FCAT_LOW_PALETTE = [
  "#EF4444", "#10B981", "#EC4899", "#0EA5E9", "#EAB308",
  "#22C55E", "#C084FC", "#F87171", "#F5A623",
]; // FCAT2 - FCAT10 (existing colors, kept as-is)

const ELITE_PALETTE = ["#06B6D4", "#8B5CF6", "#F97316", "#14B8A6", "#A855F7"]; // FCAT11-25
const CHAMPION_PALETTE = ["#1E40AF", "#7C3AED", "#B91C1C", "#047857", "#92400E"]; // FCAT26-50
const LEGEND_PALETTE = ["#0F172A", "#312E81", "#581C87", "#7F1D1D", "#1F2937"]; // FCAT51-100
const MYTHIC_GOLD = "#F5A623";   // FCAT101-200 — all variations on prestige gold/black
const MYTHIC_BLACK = "#0F172A";

function tierFor(n: number): TitleMilestone["tier"] {
  if (n <= 1) return "base";
  if (n <= 10) return "fcat-low";
  if (n <= 25) return "elite";
  if (n <= 50) return "champion";
  if (n <= 100) return "legend";
  return "mythic";
}

function colorsFor(n: number): { color: string; ribbonColor: string } {
  if (n === 0) return { color: "#92400e", ribbonColor: "#F59E0B" }; // BCAT
  if (n === 1) return { color: "#1e40af", ribbonColor: "#3B82F6" }; // DCAT (we map "FCAT0" mentally — DCAT comes from outer code)
  const idx = n - 2;
  const tier = tierFor(n);
  if (tier === "fcat-low") {
    const c = FCAT_LOW_PALETTE[idx % FCAT_LOW_PALETTE.length];
    return { color: c, ribbonColor: c };
  }
  if (tier === "elite") {
    const c = ELITE_PALETTE[idx % ELITE_PALETTE.length];
    return { color: c, ribbonColor: c };
  }
  if (tier === "champion") {
    const c = CHAMPION_PALETTE[idx % CHAMPION_PALETTE.length];
    return { color: c, ribbonColor: c };
  }
  if (tier === "legend") {
    const c = LEGEND_PALETTE[idx % LEGEND_PALETTE.length];
    return { color: c, ribbonColor: c };
  }
  // mythic (FCAT101+): pure prestige gold across the board
  return { color: MYTHIC_GOLD, ribbonColor: MYTHIC_GOLD };
}

// Generate the full title ladder up to FCAT200.
// Higher tiers get visual flair (milestone treatment every 25, tier-based palettes).
export function generateTitles(): TitleMilestone[] {
  const titles: TitleMilestone[] = [
    { name: "BCAT", points: 150, color: "#92400e", ribbonColor: "#F59E0B", tier: "base" },
    { name: "DCAT", points: 500, color: "#1e40af", ribbonColor: "#3B82F6", tier: "base" },
    { name: "FCAT", points: 1000, color: "#7e22ce", ribbonColor: "#A855F7", tier: "base" },
  ];
  for (let n = 2; n <= 200; n++) {
    const { color, ribbonColor } = colorsFor(n);
    titles.push({
      name: `FCAT${n}`,
      points: 1000 + (n - 1) * 500,
      color,
      ribbonColor,
      tier: tierFor(n),
      isMilestone: n % 25 === 0,
    });
  }
  return titles;
}

const TITLES: TitleMilestone[] = generateTitles();

// Ribbon icon everywhere — escalation comes from color depth, glow, outer ring, and overlays.
const TIER_ICON: Record<TitleMilestone["tier"], React.ComponentProps<typeof Ionicons>["name"]> = {
  base: "ribbon",
  "fcat-low": "ribbon",
  elite: "ribbon",
  champion: "ribbon",
  legend: "ribbon",
  mythic: "ribbon",
};

export function TitlesEarned({ stats }: TitlesEarnedProps) {
  const { totalPoints } = stats;
  const earned = TITLES.filter((t) => totalPoints >= t.points);
  if (earned.length === 0) return null;

  return (
    <View
      style={{
        backgroundColor: "#ffffff",
        borderRadius: 14,
        padding: 20,
        borderWidth: 1,
        borderColor: "#e5e7eb",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
        elevation: 2,
        marginBottom: 16,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <Text style={{ fontSize: 16, fontWeight: "700", color: "#374151" }}>Titles Earned</Text>
        <Text style={{ fontSize: 12, color: "#9ca3af", fontWeight: "600" }}>{earned.length} of {TITLES.length}</Text>
      </View>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
        {earned.map((title) => (
          <TitleBadge key={title.name} title={title} />
        ))}
      </View>
    </View>
  );
}

function TitleBadge({ title }: { title: TitleMilestone }) {
  const iconName = TIER_ICON[title.tier];
  const isMythic = title.tier === "mythic";
  const isLegend = title.tier === "legend";
  const isChampion = title.tier === "champion";
  const isElite = title.tier === "elite";
  const isMilestone = !!title.isMilestone;
  const customRibbon = customRibbonFor(title.name);

  // Uniform size across all tiers — escalation is purely visual (color, border, glow, overlays).
  const size = 50;
  const iconSize = 24;

  // Custom SVG ribbon — bypasses the Ionicons circle treatment entirely.
  // Render in a fixed-size square box so every ribbon occupies the exact same
  // footprint regardless of its intrinsic viewBox/aspect.
  if (customRibbon) {
    const RIBBON_BOX = 64;
    return (
      <View style={{ alignItems: "center", width: 80 }}>
        <View style={{ width: RIBBON_BOX, height: RIBBON_BOX, alignItems: "center", justifyContent: "center" }}>
          {Platform.OS === "web" ? (
            // @ts-ignore — native <img> works in RN-web
            <img
              src={customRibbon}
              alt={`${title.name} ribbon`}
              style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" } as any}
            />
          ) : (
            <Image
              source={{ uri: customRibbon }}
              style={{ width: "100%", height: "100%" }}
              resizeMode="contain"
            />
          )}
        </View>
        <Text style={{ fontSize: 11, fontWeight: "800", color: title.color, marginTop: 6 }} numberOfLines={1}>
          {title.name}
        </Text>
      </View>
    );
  }

  // Background fades from soft tint (base) to deep dramatic (mythic).
  const backgroundColor = isMythic
    ? "#0F172A"
    : isLegend
    ? `${title.ribbonColor}33`
    : isChampion
    ? `${title.ribbonColor}26`
    : isElite
    ? `${title.ribbonColor}1F`
    : `${title.ribbonColor}18`;

  // Border thickness is uniform — escalation is in glow, outer ring, and overlays.
  const borderWidth = 2;

  // Glow intensifies with tier. Champion+ all get a glow; milestone is brightest.
  const shadowOpacity = isMilestone ? 0.7 : isMythic ? 0.55 : isLegend ? 0.4 : isChampion ? 0.25 : 0;
  const shadowRadius = isMilestone ? 10 : isMythic ? 7 : isLegend ? 5 : isChampion ? 3 : 0;

  return (
    <View style={{ alignItems: "center", width: 70 }}>
      {/* Decorative outer ring on Legend+ — looks like the ribbon's pinned to a rosette. */}
      {(isLegend || isMythic || isMilestone) && (
        <View
          style={{
            position: "absolute",
            top: -3,
            width: size + 6,
            height: size + 6,
            borderRadius: (size + 6) / 2,
            borderWidth: 1.5,
            borderColor: title.ribbonColor,
            opacity: 0.4,
          }}
        />
      )}
      <View
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor,
          borderWidth,
          borderColor: title.ribbonColor,
          alignItems: "center",
          justifyContent: "center",
          shadowColor: title.ribbonColor,
          shadowOffset: { width: 0, height: 0 },
          shadowOpacity,
          shadowRadius,
        }}
      >
        <Ionicons name={iconName} size={iconSize} color={title.ribbonColor} />
        {/* Champion+ get a small star pin in the top-right (gold). Milestone gets a bigger crown. */}
        {isMilestone ? (
          <View style={{ position: "absolute", top: -6, right: -5, backgroundColor: "#F5A623", width: 18, height: 18, borderRadius: 9, alignItems: "center", justifyContent: "center", borderWidth: 1.5, borderColor: "#fff", shadowColor: "#F5A623", shadowOpacity: 0.6, shadowRadius: 3 }}>
            <Ionicons name="trophy" size={10} color="#fff" />
          </View>
        ) : isMythic ? (
          <View style={{ position: "absolute", top: -6, right: -5, backgroundColor: "#F5A623", width: 16, height: 16, borderRadius: 8, alignItems: "center", justifyContent: "center", borderWidth: 1.5, borderColor: "#fff" }}>
            <Ionicons name="star" size={9} color="#fff" />
          </View>
        ) : isLegend ? (
          <View style={{ position: "absolute", top: -5, right: -4, backgroundColor: "#fff", width: 14, height: 14, borderRadius: 7, alignItems: "center", justifyContent: "center", borderWidth: 1.5, borderColor: title.ribbonColor }}>
            <Ionicons name="star" size={8} color={title.ribbonColor} />
          </View>
        ) : null}
      </View>
      <Text
        style={{
          fontSize: 11,
          fontWeight: "800",
          color: title.color,
          marginTop: 6,
        }}
        numberOfLines={1}
      >
        {title.name}
      </Text>
    </View>
  );
}
